<template>
<v-card flat class="glass-background">
  <v-row dense class="justify-begin mt-n9 ml-1">
    <v-col dense cols="auto">
      <Headers
        :headersTitle="'Sitzplatz-Buchung'"
        :headersIcon="'mdi-clipboard-text'"
      />
    </v-col>
  </v-row>

  <v-row dense class="justify-center align-center">
    <v-col dense cols="auto">
      <DropDownPremises
        v-model="selectedPremise"
        :dropDownPremises="bookingCardPremises"
        :dropDownPremisesSeats="bookingCardSeats"
      />
    </v-col>
  </v-row>

  <v-row dense class="justify-center align-center mt-2">
    <div class="seatplan-wrapper">
      <v-card class="seat-plan-card" elevation="12">
        <img 
          :src="getSeatplanSource" 
          ref="seatImage"
          class="seat-image"
          @load="getImageSize" 
        />

        <v-tooltip
          v-for="seat in getFilteredSeats"
          :key="seat.id"
          :text="`${seat.room} Platz ${seat.name}`"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              :style="[getSeatPosition(seat), { backgroundColor: getSeatStyle(seat) }]"
              :class="['seat-btn', { 'active-seat': selectedSeat?.id === seat.id }]"
              @click="selectSeat(seat)"
            ></v-btn>
          </template>
        </v-tooltip>
      </v-card>
    </div>
  </v-row>

  <!-- Buchungs-Button -->
  <v-row dense class="justify-center align-center mt-3">
    <v-card class="bookingButtonCard" elevation="12">
      <div class="booking-content">
        <div class="booking-text">
          Ausgewählter Sitzplatz:
          <strong>{{ selectedSeat?.description || '—' }}</strong>
          <wbr>&nbsp;|
          Sitzplatzstatus:
          <strong :class="getSeatStatusClass(selectedSeat)">
            {{ getSeatStatusText(selectedSeat) }}
          </strong>
        </div>

        <v-btn
          prepend-icon="mdi-checkbox-marked-circle"
          variant="elevated"
          color="primary"
          :disabled="!selectedSeat || selectedSeat.fully_booked"
          @click="showDialogBooking"
        >
          Sitzplatz buchen
        </v-btn>
      </div>
    </v-card>
  </v-row>
</v-card>

<!-- Dialoge -->
<BookingDialog 
  ref="bookingDialogRef"
  :bookingDialogStartDate="bookingCardStartDate"
  :bookingDialogEndDate="bookingCardEndDate"
  :bookingDialogSeat="selectedSeat"
  :bookingDialogUsers="bookingCardUsers"
  @updateFavorite="$emit('updateFavorite')"
  @updateSeatStatus="$emit('updateStatus')"
  @close="handleDialogClose"
/>

</template>

<script>
import Headers from '../consumables/Headers.vue'
import DropDownPremises from '../consumables/DropDownPremises.vue'
import BookingDialog from './BookingDialog.vue'

export default {
  components: { Headers, DropDownPremises, BookingDialog },

  data() {
    return {
      selectedSeat: null,
      originalImageSize: { width: 1, height: 1 },
      selectedPremise: null,
      ignoreNextClick: false,
      dialogOpen: false
    }
  },

  emits: ['updateStatus', 'updateFavorite', 'openOccupancy'],

  props: {
    bookingCardPremises: { type: Array, default: () => [] },
    bookingCardSeats: { type: Array, default: () => [] },
    bookingCardSeatsStatus: { type: Array, default: () => [] },
    bookingCardUsers: { type: Object, required: true, default: null },
    bookingCardStartDate: { type: String, required: true },
    bookingCardEndDate: { type: String, required: true },
  },

  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },

  beforeUnmount() {
  document.removeEventListener('click', this.handleOutsideClick)
  },

  computed: {
    getSeatplanSource() {
      if (!this.selectedPremise) return ''
      return this.selectedPremise.image
    },

    getFilteredSeats() {
      if (!this.selectedPremise || !this.bookingCardSeats) return []
      return this.bookingCardSeats.filter(seat => seat.locationPath === this.selectedPremise.shortPrem)
    }
  },

  watch: {
    selectedPremise() {
      this.selectedSeat = null
      this.$emit('openOccupancy', null)
      this.$emit('updateStatus')
    },
    bookingCardStartDate() {
      this.$emit('updateStatus')
    },
    bookingCardEndDate() {
      this.$emit('updateStatus')
    }
  },

  methods: {
    selectSeat(seat) {
      // Wenn Sitz vollständig gebucht → nicht auswählbar
      if (seat.fully_booked) {
        this.$emit('openOccupancy', seat)
      }
      // Toggle-Logik: erneut klicken → Auswahl aufheben
      if (this.selectedSeat?.id === seat.id) {
        this.selectedSeat = null
        this.$emit('openOccupancy', null)
      } else {
        this.selectedSeat = seat
        this.$emit('openOccupancy', seat)
      }
    },

    showDialogBooking() {
      if (!this.selectedSeat || this.selectedSeat.fully_booked) return
      
      this.ignoreNextClick = true
      this.dialogOpen = true

      this.$nextTick(() => {
        this.$refs.bookingDialogRef.openDialogBooking()
      })
    },

    handleDialogClose() {
      this.dialogOpen = false
      this.ignoreNextClick = false
      this.selectedSeat = null
      this.$emit('openOccupancy', null)
    },

    handleOutsideClick(event) {
      if (this.dialogOpen || this.ignoreNextClick) return
      const card = this.$refs.seatImage?.closest('.seat-plan-card')
      if (card && !card.contains(event.target)) {
        if (this.selectedSeat) {
          this.selectedSeat = null
        }
      }
    },

    getSeatStyle(seat) {
      // Grau markierter Sitz
      if (seat.id === this.selectedSeat?.id) return 'rgba(140, 140, 140, 0.8)'
      if (seat.partial_booked) return 'rgba(255, 208, 0, 0.4)'
      if (seat.fully_booked) return 'rgba(255, 64, 64, 0.4)'
      return 'rgba(155, 235, 119, 0.4)'
    },

    // Gibt den Textstatus zurück
    getSeatStatusText(seat) {
      if (!seat) return '—'
      if (seat.fully_booked) return 'Belegt'
      if (seat.partial_booked) return 'Teilweise belegt'
      return 'Frei'
    },

    // Gibt die CSS-Klasse für den Status zurück
    getSeatStatusClass(seat) {
      if (!seat) return ''
      if (seat.fully_booked) return 'status-booked'
      if (seat.partial_booked) return 'status-partial'
      return 'status-free'
    },

    getOccupancy(payload) {
      this.$emit('openOccupancy', payload)
    },

    getImageSize() {
      const img = this.$refs.seatImage
      this.originalImageSize.width = img.naturalWidth
      this.originalImageSize.height = img.naturalHeight
    },

    getSeatPosition(seat) {
      const xPercent = (seat.x_pos / this.originalImageSize.width) * 100
      const yPercent = (seat.y_pos / this.originalImageSize.height) * 100
      return {
        position: 'absolute',
        top: `${yPercent}%`,
        left: `${xPercent}%`,
        transform: 'translate(-50%, -50%)',
      }
    }
  },
}
</script>

<style scoped>

.glass-background { 
  display: flex;
  height: 100%;
  max-width: fit-content;
  flex-direction: column;
  background: rgba(var(--v-theme-surface), 0.72);
  backdrop-filter: blur(8px) saturate(120%);
  border: 1px solid rgba(var(--v-theme-surface), 0.35);
  border-radius: 32px;
  overflow: visible;
  padding: 1rem;
}
.seatplan-wrapper {
  max-height: 650px;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  border-radius: 16px;
  box-shadow: 0px 6px 15px hsla(0, 0%, 0%, 0.5);
}
.seat-plan-card {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: 16px;
}
.seat-image {
  height: auto;
  width: 100%;
  object-fit: contain; /* Bild behält Seitenverhältnis */
  display: block;
}
/* -------------------------------------------------
   DARK MODE: Seatplan invertieren
   ------------------------------------------------- */
:deep(.v-theme--dark) .seat-image {
  filter: 
    invert(1)
    hue-rotate(180deg)
    brightness(0.9)
    contrast(0.75)
    saturate(0.75);
}
.seat-btn {
  position: absolute;
  background-color: rgba(255, 208, 0, 0.75);
  width: 4% !important;
  aspect-ratio: 1 / 1 !important;
  min-width: 0 !important;
  min-height: 0 !important;
  height: auto !important;
  padding: 0 !important;
  z-index: 10;
}
.seat-btn.active-seat {
  box-shadow: 0 0 6px rgba(160, 160, 160, 0.5);
  transform: scale(1.1);
  transition: all 0.15s ease-in-out;
}
.bookingButtonCard {
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  border-radius: 16px;
}
.booking-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap; /* <-- erlaubt Umbruch */
  gap: 0.5rem; /* etwas Abstand */
  padding: 0.5rem 1rem;
}
.booking-text {
  font-size: 1.15rem;
  font-weight: normal;
  color: onBackground;
  white-space: nowrap;
}
.v-btn {
  border-radius: 16px;
}       
.status-booked {
  color: #e53935; /* Rot */
  font-weight: bold;
}
.status-partial {
  color: #fbc02d; /* Gelb */
  font-weight: bold;
}
.status-free {
  color: #43a047; /* Grün */
  font-weight: bold;
}
</style>
